import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Text, Divider
} from 'components/ui';
import Loader from 'components/Loader';
import ErrorMsg from 'components/ErrorMsg';
import DefaultPaymentCheckbox from 'components/RwdCheckout/Sections/Payment/Section/DefaultPaymentCheckbox';
import {
    colors, fontSizes, fontWeights, mediaQueries, space
} from 'style/config';

/**
 * AfterPay Expanded View (L3 State) - Release 4
 *
 * Displays Afterpay payment details inline within the Payment card.
 * Replaces the legacy separate-page Afterpay flow with an inline expansion.
 *
 * @component
 */
const AfterPayExpandedView = ({
    afterpayApiData = null,
    onSaveAndContinue,
    onCancel,
    loadWidget = null,
    afterpayHeader = 'Pay with Cash App Afterpay',
    afterpaySubtitle = '',
    afterpaySaveAndContinue = 'Save and Continue',
    afterpayCancel = 'Cancel',
    afterpayLoading = 'Loading Afterpay details...',
    afterpayApiErrorFallback = 'Split your payment into 4 interest-free installments with Afterpay.',
    afterpayGiftCardWarning = '',
    afterpayLegalNotice = '',
    isLoading = false,
    errorMessage = null,
    installmentValue = '',
    isAnonymous = false
}) => {
    const headerRef = useRef(null);
    const widgetLoadedRef = useRef(false);
    const widgetContainerRef = useRef(null);
    const [widgetHasContent, setWidgetHasContent] = useState(false);

    useEffect(() => {
        if (headerRef.current) {
            headerRef.current.focus();
        }
    }, []);

    /**
     * Load Afterpay widget after component mounts
     *
     * This ensures the DOM element exists before calling the Afterpay SDK.
     * The SDK requires the target element to be present in the DOM.
     */
    useEffect(() => {
        if (loadWidget && !widgetLoadedRef.current && afterpayApiData?.widgetElementId) {
            const elementId = afterpayApiData.widgetElementId;

            widgetLoadedRef.current = true;

            // Delay to ensure DOM is fully rendered after React's commit phase
            setTimeout(() => {
                const element = document.getElementById(elementId);

                if (element) {
                    loadWidget();
                } else {
                    // Retry once more after another delay
                    setTimeout(() => {
                        const retryElement = document.getElementById(elementId);

                        if (retryElement) {
                            loadWidget();
                        }
                    }, 200);
                }
            }, 100);
        }
    }, [loadWidget, afterpayApiData?.widgetElementId]);

    /**
     * Monitor widget container for content after loading completes
     *
     * When the SDK finishes (loading becomes false), check if the widget container
     * has any content. If not, collapse the container gracefully.
     * Uses MutationObserver to detect when SDK injects content.
     */
    useEffect(() => {
        if (!afterpayApiData?.widgetElementId || isLoading) {
            return undefined;
        }

        const checkForContent = () => {
            const container = document.getElementById(afterpayApiData.widgetElementId);

            if (container && container.children.length > 0) {
                setWidgetHasContent(true);

                return true;
            }

            return false;
        };

        // Initial check
        if (checkForContent()) {
            return undefined;
        }

        // Set up MutationObserver to detect when SDK injects content
        const container = document.getElementById(afterpayApiData.widgetElementId);

        if (!container) {
            return undefined;
        }

        const observer = new MutationObserver(() => {
            if (checkForContent()) {
                observer.disconnect();
            }
        });

        observer.observe(container, { childList: true, subtree: true });

        // Fallback timeout, if no content after 3 seconds, assume failure
        const timeoutId = setTimeout(() => {
            observer.disconnect();
            // Content state remains false, which will hide the empty container
        }, 3000);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [afterpayApiData?.widgetElementId, isLoading]);

    /**
     * Renders Afterpay widget content with loading, error, and success states
     */
    const renderWidgetContent = () => {
        if (errorMessage) {
            return (
                <Box py={4}>
                    <ErrorMsg message={errorMessage} />
                    <Box
                        mt={3}
                        css={styles.fallbackText}
                    >
                        {afterpayApiErrorFallback}
                    </Box>
                </Box>
            );
        }

        // Always render the widget container so SDK can find it
        // Only the SDK widget is constrained to 312px, text content is 100% width
        // Widget wrapper only shows if SDK successfully rendered content (or is still loading)
        const showWidgetWrapper = isLoading || widgetHasContent;

        return (
            <Box css={styles.widgetArea}>
                {isLoading && (
                    <Box
                        py={3}
                        textAlign='center'
                    >
                        <Loader isShown={true} />
                        <Box
                            mt={3}
                            css={styles.loadingText}
                        >
                            {afterpayLoading}
                        </Box>
                    </Box>
                )}

                {/* Only show the styled wrapper if widget has content or is loading */}
                {/* When SDK fails to render, the container collapses gracefully */}
                {afterpayApiData?.widgetElementId && (
                    <Box
                        css={showWidgetWrapper ? styles.sdkWidgetWrapper : styles.sdkWidgetWrapperHidden}
                        ref={widgetContainerRef}
                    >
                        <div
                            id={afterpayApiData.widgetElementId}
                            data-testid='afterpay-widget-container'
                            style={isLoading ? styles.widgetContainerLoading : styles.widgetContainerLoaded}
                        />
                    </Box>
                )}

                {/* Fallback if API returns empty data or widget failed to render */}
                {/* Show fallback when: no widget ID, OR loading finished but no content rendered */}
                {((!afterpayApiData?.widgetElementId && !isLoading) || (!isLoading && !widgetHasContent && afterpayApiData?.widgetElementId)) && (
                    <Box css={styles.fallbackText}>{afterpayApiErrorFallback}</Box>
                )}
            </Box>
        );
    };

    // Format the subtitle with the installment value
    const formattedSubtitle = afterpaySubtitle?.replace('{0}', installmentValue || '');

    return (
        <Box
            role='region'
            aria-labelledby='afterpay-expanded-header'
            data-testid='afterpay-expanded-view'
        >
            {/* Header Section */}
            <Box>
                <Text
                    as='h3'
                    id='afterpay-expanded-header'
                    ref={headerRef}
                    tabIndex={-1}
                    css={styles.header}
                >
                    {afterpayHeader}
                </Text>
                {formattedSubtitle && <Text css={styles.subtitle}>{formattedSubtitle}</Text>}
            </Box>
            {/* Gift Card Warning Message */}
            {afterpayGiftCardWarning && <Box css={styles.giftCardWarning}>{afterpayGiftCardWarning}</Box>}
            {/* Widget Content Area */}
            <Box
                aria-live='polite'
                aria-busy={isLoading}
            >
                {renderWidgetContent()}
            </Box>
            {!isAnonymous && (
                <Box css={styles.checkboxContainer}>
                    <DefaultPaymentCheckbox paymentName='afterpay' />
                </Box>
            )}
            {afterpayLegalNotice && (
                <Box css={styles.legalNotice}>
                    <Text
                        is='p'
                        dangerouslySetInnerHTML={{ __html: afterpayLegalNotice }}
                    />
                </Box>
            )}
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='afterpay-cancel'
                    aria-label='Cancel Afterpay selection and return'
                >
                    {afterpayCancel}
                </Button>

                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='afterpay-save-continue'
                    aria-label='Save Afterpay and return to payment options'
                    disabled={isLoading || !!errorMessage}
                >
                    {afterpaySaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: '18px',
        marginTop: space[2],
        marginBottom: space[1]
    },
    subtitle: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '18px',
        [mediaQueries.xsMax]: {
            color: colors.gray
        }
    },
    giftCardWarning: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '14px',
        marginTop: space[2],
        marginBottom: space[2],
        [mediaQueries.xsMax]: {
            fontSize: `${fontSizes.xs}px`
        }
    },
    installmentCard: {
        backgroundColor: colors.nearWhite,
        borderRadius: 4,
        border: `1px solid ${colors.lightGray}`,
        padding: space[3],
        marginTop: space[2],
        marginBottom: space[3],
        width: '100%'
    },
    widgetArea: {
        marginTop: space[2],
        marginBottom: space[3],
        width: '100%'
    },
    sdkWidgetWrapper: {
        backgroundColor: colors.nearWhite,
        borderRadius: 4,
        border: `1px solid ${colors.lightGray}`,
        padding: space[3],
        maxWidth: 312,
        width: '100%'
    },
    sdkWidgetWrapperHidden: {
        display: 'none'
    },
    widgetContainer: {
        width: '100%',
        minHeight: 'auto'
    },
    widgetContainerLoading: {
        opacity: 0,
        height: 0,
        overflow: 'hidden'
    },
    widgetContainerLoaded: {
        opacity: 1,
        height: 'auto',
        overflow: 'visible'
    },
    loadingText: {
        fontSize: `${fontSizes.sm}px`,
        color: colors.gray
    },
    fallbackText: {
        fontSize: `${fontSizes.sm}px`,
        color: colors.gray
    },
    checkboxContainer: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    legalNotice: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: '14px',
        marginTop: space[2],
        marginBottom: space[3],
        '& a': {
            color: colors.blue,
            textDecoration: 'underline'
        },
        [mediaQueries.xsMax]: {
            fontSize: `${fontSizes.xs}px`,
            '& a': {
                textDecoration: 'none'
            }
        }
    },
    divider: {
        marginTop: space[3],
        marginBottom: space[3],
        borderColor: colors.lightGray
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: space[2],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: space[3]
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

AfterPayExpandedView.propTypes = {
    afterpayApiData: PropTypes.shape({
        widgetElementId: PropTypes.string
    }),
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    loadWidget: PropTypes.func,
    afterpayHeader: PropTypes.string,
    afterpaySubtitle: PropTypes.string,
    afterpaySaveAndContinue: PropTypes.string,
    afterpayCancel: PropTypes.string,
    afterpayLoading: PropTypes.string,
    afterpayApiErrorFallback: PropTypes.string,
    afterpayGiftCardWarning: PropTypes.string,
    afterpayLegalNotice: PropTypes.string,
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
    installmentValue: PropTypes.string,
    isAnonymous: PropTypes.bool
};

export default wrapFunctionalComponent(AfterPayExpandedView, 'AfterPayExpandedView');
